import {
  GET_LIST,
  GET_ONE,
  GET_MANY,
  GET_MANY_REFERENCE,
  CREATE,
  UPDATE,
  DELETE
} from "./types";
import {isArray} from "lodash";
import {jsonApiHttpClient, queryParameters} from "./fetch";

export default (apiUrl, httpClient = jsonApiHttpClient) => {
  /**
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The REST request params, depending on the type
   * @returns {Object} { url, options } The HTTP request parameters
   */
  const convertRESTRequestToHTTP = (type, resource, params) => {
    resource = _transformResource(type, resource);
    let url = "";
    const BASE_URL = _makeBaseURL(resource);
    const options = {};
    switch (type) {
      case GET_MANY_REFERENCE: {
        const query = _setQueries(params);
        url = new URL(`${params.baseResource}/${params.id}/${resource}?${queryParameters(query)}`, BASE_URL);        
        break;
      }
      case GET_LIST: {
        const query = _setQueries(params);
        url = new URL(`${resource}?${queryParameters(query)}`, BASE_URL).href;        
        break;
      }
      case GET_ONE: {
        url = new URL(`${resource}/${params.id}`, BASE_URL);        
        break;
      }
      case GET_MANY: {
        const query = {"filter[id]": params.ids.toString()};
        url = new URL(`${resource}?${queryParameters(query)}`, BASE_URL);        
        break;
      }
      case UPDATE: {
        url = new URL(`${resource}/${params.id}`, BASE_URL);        
        options.method = "PATCH";
        const updateParams = _constructUpdateParams(resource, params);
        options.body = JSON.stringify(updateParams);
        break;
      }
      case CREATE: {
        url = new URL(resource, BASE_URL);        
        options.method = "POST";
        const createParams = _constructCreateParams(resource, params);
        options.body = JSON.stringify(createParams);
        break;
      }
      case DELETE: {
        url = new URL(`${resource}/${params.id}`, BASE_URL);        
        options.method = "DELETE";
        break;
      }
      default: 
        throw new Error(`Unsupported fetch action type ${type}`);
    }
    return {url, options};
  };

  const _constructUpdateParams = (resource, params) => {
    let attrs = {};
    let rels = {};
    Object.keys(params.data).forEach(
      key => {
        if(key.endsWith("region_id")){
          const rel = key.replace("_id", "");
          rels[rel] = {data: {type: rel + "s", id: params.data[key]}}

        }
        attrs[key] = params.data[key];
      }
    );
    const updateParams = {
      data: {type: resource, id: params.id, attributes: attrs, relationships: rels}
    };
    return updateParams;
  };

  const _constructCreateParams = (resource, params) => {
    const createParams = {
      data: {type: resource, attributes: params.data}
    };
    return createParams;
  };

  const _transformResource = (type, resource) => {
    if(resource === "gift_cards" && type === "GET_LIST"){
      const kiosk = JSON.parse(localStorage.getItem("kiosk"));
      const kiosk_id = kiosk ? kiosk.id : '';
      resource = kiosk ? `kiosks/${kiosk_id}/gift_cards` : '';
      return resource;
    }
    return resource;
  };

  const _setQueries = params => {
    const {page, perPage} = params.pagination;
    const {field, order} = params.sort;
    const query = {
      "page[size]": perPage,
      "page[number]": page
    };
    Object.keys(params.filter).forEach(key => {
      const filterField = "filter[" + key + "]";
      query[filterField] = params.filter[key];
    });
    if (order === "ASC") {
      query.sort = field;
    } else {
      query.sort = "-" + field;
    }
    return query;
  };

  const _makeBaseURL = () => {    
    const trailingSlashRegex = /(\/)$/g;
    if(apiUrl.match(trailingSlashRegex) === null) {
      apiUrl += '/';
    }
    return new URL(apiUrl);
  };

  const transformData = dic => {
    let interDic = Object.assign({id: dic.id}, dic.attributes, dic.meta);
    if (dic.relationships) {
      Object.keys(dic.relationships).forEach(function(key) {
        let keyString = key + "_id";
        if (dic.relationships[key].data) {
          if (isArray(dic.relationships[key].data)) {
            interDic[keyString] = dic.relationships[key].data.map(
              rel => rel.id
            );
          } else {
            interDic[keyString] = dic.relationships[key].data.id;
          }
        }
      });
    }
    return interDic;
  };

  const mapData = data => {
    return data.map ? data.map(transformData) : transformData(data);
  };

  /**
   * @param {Object} response HTTP response from fetch()
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The REST request params, depending on the type
   * @returns {Object} REST response
   */
  const convertHTTPResponseToREST = (response, type, resource, params) => {
    const {json} = response;
    switch (type) {
      case GET_LIST:
      case GET_MANY:
      case GET_MANY_REFERENCE:
      case GET_ONE:
        const result = {data: mapData(json.data)};
        if (json.meta.total !== undefined) {
          result.total = json.meta["total"];
        }
        return result;
      case UPDATE:
      case CREATE:
        return {
          data: Object.assign({id: json.data.id}, json.data.attributes)
        };
      case DELETE:
        return {data: json};
      default:
        throw new Error(`Unsupported fetch response type ${type}`);
    }
  };

  /**
   * @param {string} type Request type, e.g GET_LIST
   * @param {string} resource Resource name, e.g. "posts"
   * @param {Object} payload Request parameters. Depends on the request type
   * @returns {Promise} the Promise for a REST response
   */
  return (type, resource, params) => {
    const {url, options} = convertRESTRequestToHTTP(type, resource, params);
    return httpClient(url, options).then(response =>
      convertHTTPResponseToREST(response, type, resource, params)
    );
  };
  };



// WEBPACK FOOTER //
// ./src/aor-jsonapi-client/restClient.js